import { createTheme, ThemeProvider, type ThemeProviderProps } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { cn } from '@tempo-io/sui-class-utils';
import { KeyboardArrowDown } from '@tempo-io/sui-icons';
import type { ReactNode } from 'react';

declare module '@mui/material/styles' {
  interface Palette {
    activityCircle: string;
  }
  interface PaletteOptions {
    activityCircle?: string;
  }
  interface TypeBackground {
    activityCircle?: string;
  }
}

// SUI theme color mappings from OKLCH to hex/rgba
// --background: oklch(1.000 0.000 0.00) -> #FFFFFF
// --foreground: oklch(0.2103 0.0059 285.89) -> #18181B
// --card: oklch(1.000 0.000 0.00) -> #FFFFFF
// --muted: oklch(0.9674 0.0013 286.37) -> #F4F4F5
// --border: oklch(0.9197 0.004 286.32) -> #E4E4E7
// --primary: oklch(0.2103 0.0059 285.89) -> #18181B
// --destructive: oklch(0.5771 0.2152 27.33) -> #EF4444
// --radius: 0.625rem -> 10px

// Tailwind CSS color palette as CSS custom properties
export const tailwindColors = {
  white: '#ffffff',

  // Sky colors (used for secondary)
  sky50: '#f0f9ff',
  sky100: '#e0f2fe',
  sky200: '#bae6fd',
  sky300: '#7dd3fc',
  sky400: '#38bdf8',
  sky500: '#0ea5e9',
  sky600: '#0284c7',
  sky700: '#0369a1',
  sky800: '#075985',
  sky900: '#0c4a6e',

  // Zinc colors (used for text/foreground)
  zinc50: '#fafafa',
  zinc100: '#f4f4f5',
  zinc200: '#e4e4e7',
  zinc300: '#d4d4d8',
  zinc400: '#a1a1aa',
  zinc500: '#71717a',
  zinc600: '#52525b',
  zinc700: '#3f3f46',
  zinc800: '#27272a',
  zinc900: '#18181b',

  // Red colors (used for destructive/error)
  red50: '#fef2f2',
  red100: '#fee2e2',
  red200: '#fecaca',
  red300: '#fca5a5',
  red400: '#f87171',
  red500: '#ef4444',
  red600: '#dc2626',
  red700: '#b91c1c',
  red800: '#991b1b',
  red900: '#7f1d1d',
};

export const muiTheme = createTheme({
  /**
   * Centralized MUI theme for Nexus frontend.
   *
   * Currently used by:
   * - Feedback & Idea Management (FIM) module
   * - Future: Swimlane component and other MUI-dependent features
   *
   * Customizations:
   * - Button: text-transform set to 'capitalize' (no uppercase)
   * - IconButton: borderRadius 8px, padding 6px
   * - Typography: System font stack with 14px base size
   * - Spacing: Base unit of 4px
   * - Shape: borderRadius 8px
   * - Palette: Custom text and primary colors for FIM design
   */
  components: {
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <KeyboardArrowDown />,
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        inputRoot: {
          '&.MuiInputBase-sizeSmall': {
            paddingTop: '2.5px',
            paddingBottom: '2.5px',
            paddingLeft: '6px',
          },
        },
        tag: {
          borderRadius: 12,
        },
        option: {
          // Options are shown as "selected" (aria-selected=true) when already added as members,
          // but we don't want the default MUI gray selected highlight in this list.
          '&[aria-selected="true"]': {
            backgroundColor: '#FFFFFF !important',
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: `${tailwindColors.zinc100} !important`,
          },
          '&:hover': {
            backgroundColor: `${tailwindColors.zinc100} !important`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${tailwindColors.zinc200}`, // --border
          backgroundColor: '#FFFFFF', // --card
          boxShadow: '0px 1px 3px 0px rgba(9, 9, 11, 0.10), 0px 1px 2px 0px rgba(9, 9, 11, 0.06)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6, // --radius (0.625rem = 10px)
          borderColor: tailwindColors.zinc200, // --border / --input
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tailwindColors.zinc900, // --ring
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tailwindColors.zinc900, // --ring
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 6, // --radius
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          whiteSpace: 'nowrap',
          borderRadius: 6,
          fontSize: '0.875rem', // 14px to match SUI
          padding: '3.5px 12px',
        },
        contained: {
          backgroundColor: tailwindColors.zinc900, // --primary
          color: '#FFFFFF', // --primary-foreground
          '&:hover': {
            backgroundColor: tailwindColors.zinc800,
          },
        },
        outlined: {
          borderColor: tailwindColors.zinc200, // --border
          color: tailwindColors.zinc900, // --foreground
          '&:hover': {
            borderColor: tailwindColors.zinc900,
            backgroundColor: tailwindColors.zinc100, // --muted
          },
        },
        text: {
          color: tailwindColors.zinc900, // --foreground
          '&:hover': {
            backgroundColor: tailwindColors.zinc100, // --muted
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6, // --radius
          padding: 6,
          '&:hover': {
            backgroundColor: tailwindColors.zinc100, // --muted
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 4,
          color: tailwindColors.zinc200, // --border
          '&.Mui-checked': {
            color: tailwindColors.zinc900, // --primary
          },
          '&.Mui-disabled': {
            color: tailwindColors.zinc100,
          },
          '& svg': {
            borderRadius: 6, // smaller radius for checkboxes
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          background: tailwindColors.zinc200, // --muted
          color: tailwindColors.zinc900, // --foreground
          fontSize: '0.875rem',
          padding: '4px',
        },
        outlined: {
          backgroundColor: 'transparent',
          borderColor: tailwindColors.zinc200,
        },
        outlinedSecondary: {
          backgroundColor: tailwindColors.sky50,
          borderColor: tailwindColors.sky500,
          color: tailwindColors.sky700,
          '& .MuiChip-deleteIcon': {
            color: tailwindColors.sky700,
            '&:hover': {
              color: tailwindColors.sky800,
            },
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ({ className, ...props }) => (
          <KeyboardArrowDown className={cn(className, '!top-auto')} {...props} />
        ),
      },
      styleOverrides: {
        root: {
          borderRadius: 6, // --radius
        },
        select: {
          '&.MuiOutlinedInput-input': {
            paddingTop: '6.5px',
            paddingBottom: '6.5px',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 6, // --radius
          backgroundColor: '#FFFFFF', // --card
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // --background
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        rowHeight: 35,
        columnHeaderHeight: 40,
      },
      styleOverrides: {
        root: {
          // Outer container styling to match Tempo SUI table
          borderColor: tailwindColors.zinc200,
          overflow: 'hidden',
          fontSize: '0.875rem', // 14px (text-sm)

          // Drive all of MUI's internal var(--DataGrid-rowBorderColor) references
          // (withLeftBorder, withRightBorder, row borders, etc.) to our zinc200 token.
          // This is the single source of truth for grid border color.
          '--DataGrid-rowBorderColor': tailwindColors.zinc200,
          '--DataGrid-columnHeaderBorderColor': tailwindColors.zinc200,

          '& .MuiDataGrid-columnHeader': {
            padding: '0 8px', // px-2 (matches Tempo SUI)
            color: tailwindColors.zinc900,
            fontWeight: 500, // font-medium
            fontSize: '0.875rem', // 14px

            '&:focus, &:focus-within': {
              outline: 'none',
            },
            '&:focus-visible': {
              outline: `2px solid ${tailwindColors.zinc900}`,
              outlineOffset: '-2px',
            },
          },

          // Reorderable column headers: grab while hovering / pressing.
          // MUI applies cursor: pointer to sortable headers; override it on the
          // draggable container (only present when column reorder is enabled).
          // Note: once the native HTML5 drag starts, the browser controls the
          // cursor (dropEffect: 'move') and CSS cursor is ignored — so a
          // `grabbing` cursor during the actual drag isn't achievable here.
          '& .MuiDataGrid-columnHeaderDraggableContainer[draggable="true"]': {
            cursor: 'grab',

            '&:active': {
              cursor: 'grabbing',
            },
          },

          // Vertical borders for headers (only when showColumnVerticalBorder={true})
          '& .MuiDataGrid-columnHeader--withRightBorder': {
            borderRightColor: tailwindColors.zinc200,
          },

          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 500, // font-medium
            fontSize: '14px',
            color: tailwindColors.zinc900,
            whiteSpace: 'nowrap', // whitespace-nowrap
          },

          // Sorted column header: highlight whole cell + title in blue,
          // and keep the sort arrow visible at all times in a white circle.
          '& .MuiDataGrid-columnHeader--sorted': {
            backgroundColor: 'var(--color-bg-accent-blue-subtle)',
            // boxShadow (not borderBottom) so it paints without changing box
            // height, which would otherwise get clipped by the fixed-height
            // header row. --color-border-accent-blue is a 20%-opacity tint,
            // too faint here, so use the solid --color-bg-accent-blue instead.
            boxShadow: 'inset 0 -2px 0 0 var(--color-bg-accent-blue)',

            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'var(--color-text-accent-blue)',
            },

            // Some columns render custom header content (icon + text) via a
            // Tailwind-colored span instead of MuiDataGrid-columnHeaderTitle
            // (see HeaderWithIconFormatter), which needs its own override.
            '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainer *':
              {
                color: 'var(--color-text-accent-blue) !important',
              },

            '& .MuiDataGrid-iconButtonContainer': {
              visibility: 'visible',
              width: 'auto',
            },

            // Keep the button's default (unsorted/hover) box size and padding
            // so the arrow sits the same distance from the title text as it
            // does when the column isn't sorted — only add the white circle.
            '& .MuiDataGrid-sortButton': {
              opacity: 1,
              backgroundColor: '#FFFFFF',
              borderRadius: '50%',

              '& .MuiDataGrid-sortIcon': {
                color: 'var(--color-icon-accent-blue)',
              },
            },
          },

          '& .MuiDataGrid-row': {
            // Hover state - matches Tempo SUI hover:bg-bg-neutral/50
            '&:hover': {
              backgroundColor: `${tailwindColors.zinc100}80`, // bg-bg-neutral/50 (zinc100 with 50% opacity)
            },

            // Selected state - matches Tempo SUI data-[state=selected]:bg-bg-neutral
            '&.Mui-selected': {
              backgroundColor: tailwindColors.zinc100, // bg-bg-neutral

              '&:hover': {
                backgroundColor: `${tailwindColors.zinc100}cc`, // Slightly darker on hover
              },
            },

            // Remove border from last row (matches Tempo SUI)
            '&:last-child': {
              borderBottom: 'none',
            },
          },

          '& .MuiDataGrid-cell': {
            padding: '8px', // p-2 (matches Tempo SUI)
            fontSize: '0.875rem', // 14px
            color: tailwindColors.zinc900,
            whiteSpace: 'nowrap', // whitespace-nowrap
            display: 'flex',
            alignItems: 'center', // align-middle

            '&:focus, &:focus-within': {
              outline: 'none',
            },
            '&:focus-visible': {
              outline: `2px solid ${tailwindColors.zinc900}`,
              outlineOffset: '-2px',
            },
          },

          // Vertical borders for cells (only when showCellVerticalBorder={true})
          '& .MuiDataGrid-cell--withRightBorder': {
            borderRightColor: tailwindColors.zinc200,
          },

          '& .MuiDataGrid-cellContent': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },

          '& .MuiDataGrid-virtualScroller': {
            overflow: 'auto',
          },

          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${tailwindColors.zinc200}`,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 34,
        },
        indicator: {
          height: 2,
          backgroundColor: tailwindColors.zinc900,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0px 0px 6px',
          gap: '8px',
          minHeight: 34,
          minWidth: 'fit-content',
          marginRight: '2rem',
          height: '18px',

          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.2px',
          color: tailwindColors.zinc900,
          textTransform: 'none',

          '&.Mui-selected': {
            color: tailwindColors.zinc900,
            fontWeight: 500,
          },

          '&[aria-selected="false"]': {
            color: tailwindColors.zinc500,
          },

          '&.Mui-disabled': {
            opacity: 0.5,
            color: tailwindColors.zinc900,
          },

          '&:hover': {
            color: tailwindColors.zinc900,
            opacity: 0.8,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tailwindColors.zinc900,
          color: '#FFFFFF',
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },
  },
  palette: {
    text: {
      primary: tailwindColors.zinc900, // --foreground
      secondary: tailwindColors.zinc500, // --muted-foreground
    },
    primary: {
      main: tailwindColors.zinc900, // --primary
      contrastText: '#FFFFFF', // --primary-foreground
    },
    secondary: {
      main: tailwindColors.sky700, // --secondary
      contrastText: '#FFFFFF', // --secondary-foreground
    },
    error: {
      main: tailwindColors.red500, // --destructive
    },
    background: {
      default: '#FFFFFF', // --background
      paper: '#FFFFFF', // --card
      activityCircle: '#00BBC6',
    },
    divider: tailwindColors.zinc200, // --border
  },
  shape: {
    borderRadius: 6, // --radius (0.625rem = 10px)
  },
  spacing: 4,
  typography: {
    fontSize: 14,
    subtitle1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 600,
      textWrap: 'nowrap',
    },
    subtitle2: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 600,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontSize: '0.875rem',
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    fontFamily:
      'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  },
});

type MuiThemeProviderProps = Omit<ThemeProviderProps, 'theme'> & {
  theme?: ThemeProviderProps['theme'];
  children: ReactNode;
};

/**
 * Extensible MUI ThemeProvider wrapper for Nexus frontend.
 *
 * Provides the centralized MUI theme to all child components while allowing
 * theme overrides via props if needed.
 *
 * @example
 * // Basic usage (uses default muiTheme)
 * <MuiThemeProvider>
 *   <App />
 * </MuiThemeProvider>
 *
 * @example
 * // With custom theme override (rare, for special cases)
 * <MuiThemeProvider theme={customTheme}>
 *   <SpecialComponent />
 * </MuiThemeProvider>
 */
export const MuiThemeProvider = ({
  theme = muiTheme,
  children,
  ...props
}: MuiThemeProviderProps) => {
  return (
    <ThemeProvider theme={theme} {...props}>
      {children}
    </ThemeProvider>
  );
};
